/**
 * Rug PDP behaviour (templates/product.rug.json).
 *
 * Works alongside the theme's own components rather than replacing them:
 * variant-selector-component still picks the variant and product-form-component
 * still adds to cart. This file adds the "no size chosen yet" state, the rug
 * labels, stock line, underlay add-on, size guide and mobile sticky bar.
 */

import { ThemeBus } from "@theme/events-bus";
import { EVENTS } from "@theme/event-constants";
import { shopifyCartUrl, updateCartBubble } from "@theme/utilities";
import { dragClickShouldSuppress } from "@theme/pdp-ios-guard";

/* ------------------------------------------------------------------ */
/* Market helpers: units, number/money format and copy.                */
/* Settings come from snippets/rr-pdp-config.liquid (#rr-pdp-config).  */
/* ------------------------------------------------------------------ */

const RR = (() => {
  let cfg = {};
  try {
    cfg = JSON.parse(document.getElementById("rr-pdp-config")?.textContent || "{}");
  } catch {
    cfg = {};
  }
  const strings = cfg.strings || {};
  const imperial = cfg.units === "imperial";
  const dec = cfg.decimal || ".";
  const sp = cfg.unitSpace || "";

  /** Copy from rr_pdp locales, with {placeholders} filled in. */
  const t = (key, vars = {}, fallback = "") => {
    const raw = strings[key] ?? fallback ?? key;
    return String(raw).replace(/\{(\w+)\}/g, (m, k) => (k in vars ? String(vars[k]) : m));
  };

  const num = (n, dp = 1) => {
    const s = String(Math.round(Number(n) * 10 ** dp) / 10 ** dp);
    return dec === "." ? s : s.replace(".", dec);
  };

  /** Strip "Select Width:" style prefixes and tidy spacing. */
  const clean = (label) =>
    String(label || "")
      .replace(/^.*:\s*/, "")
      .replace(/(\d)\s*inch(es)?\b/gi, "$1in")
      .replace(/\s+/g, " ")
      .trim();

  const isImperialLabel = (label) => /\d\s*(ft|feet|foot|in\b|inch|'|"|’|″)/i.test(String(label || ""));

  /** Any size label to centimetres: 60cm, 70 cm, 10.2 m, 10,2 m, 8mm, 2ft 4in, 5' 3", 1ft. */
  const toCm = (label) => {
    const s = clean(label).toLowerCase().replace(/,/g, ".").replace(/[’′]/g, "'").replace(/[”″]/g, '"');
    if (!s) return 0;
    if (isImperialLabel(s)) {
      const ft = s.match(/(\d+(?:\.\d+)?)\s*(?:ft|feet|foot|')/);
      const inch = s.match(/(\d+(?:\.\d+)?)\s*(?:in(?:ch(?:es)?)?\b|"|$)/);
      const ftVal = ft ? Number(ft[1]) : 0;
      let inVal = inch ? Number(inch[1]) : 0;
      if (ft && inch && inch.index === ft.index) inVal = 0;
      return Math.round((ftVal * 12 + inVal) * 2.54);
    }
    const compact = s.replace(/\s/g, "");
    const n = parseFloat(compact);
    if (!Number.isFinite(n)) return 0;
    if (/mm$/.test(compact)) return Math.round(n / 10);
    if (/cm$/.test(compact)) return Math.round(n);
    if (/m$/.test(compact)) return Math.round(n * 100);
    return Math.round(n);
  };

  const ftIn = (cm) => {
    const inches = Math.round(cm / 2.54);
    const f = Math.floor(inches / 12);
    const i = inches % 12;
    if (!f) return `${i}in`;
    return i ? `${f}ft ${i}in` : `${f}ft`;
  };
  const metric = (cm) => (cm >= 1000 ? `${num(cm / 100)}${sp}m` : `${Math.round(cm)}${sp}cm`);
  /** A length in the market's units (runner lengths, rug sides). */
  const len = (cm) => (imperial ? ftIn(cm) : metric(cm));
  /** A small gap or border, e.g. 45cm / 18in. */
  const gap = (cm) => (imperial ? `${Math.round(cm / 2.54)}in` : `${Math.round(cm)}${sp}cm`);
  /** A two-dimension size, e.g. 160 × 230cm / 5ft 3in × 7ft 7in. */
  const size = (w, l) => (imperial ? `${ftIn(w)} × ${ftIn(l)}` : `${Math.round(w)} × ${Math.round(l)}${sp}cm`);
  /** Room size in metres or feet. */
  const room = (w, l) => (imperial ? `${ftIn(w)} × ${ftIn(l)}` : `${num(w / 100)} × ${num(l / 100)}${sp}m`);
  /** Show a variant's own size label in a tidy, market-correct way. */
  const label = (raw) => {
    const c = clean(raw);
    if (!c) return "";
    if (isImperialLabel(c) || /\dmm$/i.test(c.replace(/\s/g, ""))) return c;
    const cm = toCm(c);
    return cm ? metric(cm) : c;
  };

  /** Format pence/cents the same way Liquid's money filter does for this market. */
  const sample = String(cfg.moneySample || "");
  const sampleMatch = sample.match(/1(\D?)234(\D)56/);
  const money = (cents) => {
    const value = (Number(cents) || 0) / 100;
    if (sampleMatch) {
      const [all, thousands, decimal] = sampleMatch;
      const [i, f] = value.toFixed(2).split(".");
      const int = i.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return sample.replace(all, `${int}${decimal}${f}`);
    }
    return new Intl.NumberFormat(cfg.dateLocale || window.shopLocale || "en-GB", {
      style: "currency",
      currency: window.shopCurrency || "GBP",
    }).format(value);
  };

  const date = (d) =>
    d.toLocaleDateString(cfg.dateLocale || "en-GB", { weekday: "short", day: "numeric", month: "short" });

  return { cfg, t, imperial, clean, isImperialLabel, toCm, ftIn, metric, len, gap, size, room, label, money, num, date };
})();

const UNIT_KEY = "rug-units";
const money = (pence) => RR.money(pence);

/* Formatters that follow the cm / ft toggle on the rug page (defaults to the market's units). */
const fmt = (units) => {
  const ft = units === "ft";
  return {
    gap: (cm) => (ft ? `${Math.round(cm / 2.54)}in` : `${Math.round(cm)}${RR.cfg.unitSpace || ""}cm`),
    size: (w, l) => (ft ? `${RR.ftIn(w)} × ${RR.ftIn(l)}` : `${Math.round(w)} × ${Math.round(l)}${RR.cfg.unitSpace || ""}cm`),
    short: (w, l) => (ft ? `${RR.ftIn(w)} × ${RR.ftIn(l)}` : `${Math.round(w)} × ${Math.round(l)}`),
    room: (w, l) => (ft ? `${RR.ftIn(w)} × ${RR.ftIn(l)}` : `${RR.num(w / 100)} × ${RR.num(l / 100)}${RR.cfg.unitSpace || ""}m`),
  };
};

function goodFor(w, l) {
  const short = Math.min(w, l);
  const long = Math.max(w, l);
  const area = (short * long) / 10000;
  if (!short || !long) return "";
  if (short <= 80 && long >= 200) return RR.t("good_bed_hall");
  if (long <= 130) return RR.t("good_doorway");
  if (area < 1.5) return RR.t("good_small");
  if (area < 3) return RR.t("good_coffee");
  if (area < 5) return RR.t("good_living");
  return RR.t("good_large");
}

function addWorkingDays(days) {
  const d = new Date();
  let left = days;
  while (left > 0) {
    d.setDate(d.getDate() + 1);
    const day = d.getDay();
    if (day !== 0 && day !== 6) left -= 1;
  }
  return RR.date(d);
}

function readProduct() {
  const el = document.getElementById("product-json");
  try {
    return el ? JSON.parse(el.textContent) : null;
  } catch {
    return null;
  }
}

/* ------------------------------------------------------------------ */
/* Room planner (Rug Size Guide v2 handoff)                             */
/* ------------------------------------------------------------------ */

const ROOMS = [
  ["living", RR.t("room_living")],
  ["bedroom", RR.t("room_bedroom")],
  ["dining", RR.t("room_dining")],
];
const TIER_LABEL = { Ideal: RR.t("tier_ideal"), Good: RR.t("tier_good"), Accent: RR.t("tier_accent"), Compact: RR.t("tier_compact") };

const TIERS = {
  Ideal: { fg: "#088C62", bg: "#e8f4ee" },
  Good: { fg: "#1C3956", bg: "#f1f4f7" },
  Accent: { fg: "#1C3956", bg: "#f4f1ec" },
  Compact: { fg: "#6B6B6B", bg: "#f4f1ec" },
};

const TIPS = (f) => ({
  living: [
    RR.t("tip_living_1"),
    RR.t("tip_living_2", { a: f.gap(30), b: f.gap(45) }),
    RR.t("tip_living_3"),
  ],
  bedroom: [
    RR.t("tip_bedroom_1"),
    RR.t("tip_bedroom_2", { a: f.gap(45), b: f.gap(60) }),
    RR.t("tip_bedroom_3", { size: f.size(160, 230) }),
  ],
  dining: [
    RR.t("tip_dining_1", { a: f.gap(60) }),
    RR.t("tip_dining_2"),
    RR.t("tip_dining_3"),
  ],
});

function place(room, S, L, f = fmt(RR.imperial ? "ft" : "cm")) {
  if (room === "living") {
    if (L >= 190 && S >= 150) {
      const r = { x: 230 - L / 2, y: 80, w: L, h: S };
      return L >= 260
        ? { ...r, tier: "Ideal", title: RR.t("plan_living_joined_t"), body: RR.t("plan_living_joined_b") }
        : { ...r, tier: "Good", title: RR.t("plan_living_classic_t"), body: RR.t("plan_living_classic_b") };
    }
    if (S <= 70) return { x: 230 - L / 2, y: 141, w: L, h: S, tier: "Compact", title: RR.t("plan_living_hall_t"), body: RR.t("plan_living_hall_b") };
    if (L >= 150) return { x: 230 - L / 2, y: 175 - S / 2, w: L, h: S, tier: "Accent", title: RR.t("plan_living_front_t"), body: RR.t("plan_living_front_b", { size: f.size(160, 230) }) };
    return { x: 230 - L / 2, y: 175 - S / 2, w: L, h: S, tier: "Compact", title: RR.t("plan_living_accent_t"), body: RR.t("plan_living_accent_b") };
  }
  if (room === "bedroom") {
    if (L >= 210 && S >= 120) {
      const r = { x: 230 - L / 2, y: 70, w: L, h: S };
      const side = (L - 150) / 2;
      return L >= 270
        ? { ...r, tier: "Ideal", title: RR.t("plan_bed_frame_t"), body: RR.t("plan_bed_frame_b", { side: f.gap(side) }) }
        : { ...r, tier: "Good", title: RR.t("plan_bed_step_t"), body: RR.t("plan_bed_step_b", { side: f.gap(side) }) };
    }
    if (L >= 150) return { x: 230 - L / 2, y: 215, w: L, h: S, tier: "Accent", title: RR.t("plan_bed_foot_t"), body: RR.t("plan_bed_foot_b") };
    return { x: 150 - S, y: 70, w: S, h: L, tier: "Accent", title: RR.t("plan_bed_side_t"), body: RR.t("plan_bed_side_b") };
  }
  const mx = (L - 180) / 2;
  const my = (S - 90) / 2;
  const m = Math.min(mx, my);
  const r = { x: 230 - L / 2, y: 190 - S / 2, w: L, h: S };
  if (m >= 60) return { ...r, tier: "Ideal", title: RR.t("plan_dining_stay_t"), body: RR.t("plan_dining_stay_b", { gap: f.gap(60) }) };
  if (m >= 45) return { ...r, tier: "Good", title: RR.t("plan_dining_set_t"), body: RR.t("plan_dining_set_b", { gap: f.gap(m) }) };
  if (m >= 0) return { ...r, tier: "Accent", title: RR.t("plan_dining_under_t"), body: RR.t("plan_dining_under_b", { size: f.size(200, 290) }) };
  return { ...r, tier: "Compact", title: RR.t("plan_dining_centre_t"), body: RR.t("plan_dining_centre_b", { size: f.size(200, 290), table: f.size(180, 90) }) };
}

/* ------------------------------------------------------------------ */
/* <rug-size-selector>: owns the page-level rug state                  */
/* ------------------------------------------------------------------ */

class RugSizeSelector extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;

    this.form = this.closest("form");
    this.productForm = this.closest("product-form-component");
    this.product = readProduct();
    this.buttons = [...this.querySelectorAll("[data-variant-option]")];
    this.heading = this.querySelector("[data-rug-size-heading]");
    this.headingDefault = this.heading?.textContent.trim() || RR.t("size");
    this.good = this.querySelector("[data-rug-good]");
    this.goodText = this.querySelector("[data-rug-good-text]");
    this.drawer = this.querySelector("[data-rug-guide]");
    this.planner = this.querySelector("[data-rug-planner]");
    this.guideCta = this.querySelector("[data-rug-guide-cta]");
    this.rugHex = this.dataset.rugHex;
    this.room = "living";
    let saved = null;
    try {
      saved = localStorage.getItem(UNIT_KEY);
    } catch {
      saved = null;
    }
    const fallback = this.dataset.defaultUnits === "ft" ? "ft" : "cm";
    this.units = saved === "ft" || saved === "cm" ? saved : fallback;

    const initial = this.dataset.initialVariant;
    this.chosen = this.buttons.length <= 1 || Boolean(initial && this.#buttonForVariant(initial));
    this.variant = this.#variantFromDom();
    if (!this.chosen) this.#stripVariantParam();

    this.#drawShapes();
    this.#applyUnits();

    if (this.drawer) {
      document.body.appendChild(this.drawer);
      this.drawer.addEventListener("click", this.#onClick);
    }
    this.addEventListener("click", this.#onClickCapture, true);
    this.addEventListener("click", this.#onClick);
    ThemeBus.addEventListener(EVENTS.variantChange, this.#onVariantChange);
    document.addEventListener("submit", this.#onSubmitCapture, true);
    document.addEventListener("keydown", this.#onKeydown);
    document.addEventListener("click", this.#onDocumentClick);

    const onQty = (e) => {
      if (e.target.matches('[name="quantity"]')) this.render();
    };
    this.form?.addEventListener("input", onQty);
    this.form?.addEventListener("change", onQty);
    document.addEventListener("rug:underlay-change", () => this.render());

    this.#watchCartButton();
    window.RugPDP = this;
    this.render();
  }

  get quantity() {
    return Math.max(1, parseInt(this.form?.querySelector('[name="quantity"]')?.value, 10) || 1);
  }

  get selectedButton() {
    if (!this.chosen || !this.variant) return null;
    return this.#buttonForVariant(this.variant.id);
  }

  get sizeLabel() {
    const btn = this.selectedButton;
    return btn ? this.#labelFor(btn) : "";
  }

  #buttonForVariant(id) {
    return this.buttons.find((b) => b.dataset.variantId === String(id)) || null;
  }

  #stripVariantParam() {
    const url = new URL(location.href);
    if (!url.searchParams.has("variant")) return;
    url.searchParams.delete("variant");
    history.replaceState({}, "", url);
  }

  #variantFromDom() {
    const id = this.form?.querySelector('[name="id"]')?.value;
    return this.product?.variants?.find((v) => String(v.id) === String(id)) || this.product?.variants?.[0] || null;
  }

  #labelFor(btn) {
    const w = +btn.dataset.w;
    const l = +btn.dataset.l;
    if (this.units === "ft") {
      if (btn.dataset.labelFt) return btn.dataset.labelFt;
      if (w && l) return fmt("ft").size(w, l);
    }
    if (w && l) return fmt("cm").size(w, l);
    return btn.dataset.labelCm;
  }

  #drawShapes() {
    this.buttons.forEach((btn) => {
      const w = +btn.dataset.w;
      const l = +btn.dataset.l;
      const shape = btn.querySelector("[data-rug-shape]");
      if (!shape) return;
      if (!w || !l) {
        shape.parentElement.hidden = true;
        return;
      }
      const h = Math.min(26, 26 * Math.sqrt(l / 290));
      shape.style.height = `${h.toFixed(1)}px`;
      shape.style.width = `${Math.max(9, (h * w) / l).toFixed(1)}px`;
    });
  }

  #applyUnits() {
    this.querySelectorAll("[data-rug-unit]").forEach((b) => {
      b.setAttribute("aria-pressed", String(b.dataset.rugUnit === this.units));
    });
    this.buttons.forEach((btn) => {
      const dims = btn.querySelector("[data-rug-dims]");
      if (dims) dims.textContent = this.#labelFor(btn);
    });
  }

  #onClickCapture = (e) => {
    const btn = e.target.closest("[data-variant-option]");
    if (!btn || !this.contains(btn)) return;
    this.chosen = true;
    this.classList.remove("is-nudged");
  };

  #onClick = (e) => {
    const unit = e.target.closest("[data-rug-unit]");
    if (unit) {
      this.units = unit.dataset.rugUnit;
      try {
        localStorage.setItem(UNIT_KEY, this.units);
      } catch {
        /* private mode */
      }
      this.#applyUnits();
      this.render();
      if (!this.drawer.hidden) this.#renderPlanner();
      return;
    }
    if (e.target.closest("[data-rug-guide-open]")) {
      this.openGuide();
      return;
    }
    if (e.target.closest("[data-rug-guide-close]")) {
      this.closeGuide();
      return;
    }
    const tab = e.target.closest("[data-rug-room]");
    if (tab) {
      this.room = tab.dataset.rugRoom;
      this.#renderPlanner();
      return;
    }
    const chip = e.target.closest("[data-rug-plan-size]");
    if (chip) {
      this.planId = chip.dataset.rugPlanSize;
      this.#renderPlanner();
      return;
    }
    if (e.target.closest("[data-rug-guide-cta]")) {
      const btn = this.#buttonForVariant(this.planId);
      if (btn && btn !== this.selectedButton) btn.click();
      this.closeGuide();
    }
  };

  #onDocumentClick = (e) => {
    const pick = e.target.closest("[data-rug-pick-size]");
    if (pick) {
      const btn = this.buttons.find((b) => b.dataset.value === pick.dataset.rugPickSize);
      if (btn) {
        btn.click();
        this.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }
    if (e.target.closest("[data-rug-sticky-cta]")) {
      if (this.#state() === "notify") return;
      const submit = this.form?.querySelector('add-to-cart-component button[type="submit"]');
      if (submit && !submit.disabled) submit.click();
      else this.nudge();
    }
  };

  #onKeydown = (e) => {
    if (e.key === "Escape" && this.drawer && !this.drawer.hidden) this.closeGuide();
  };

  #onVariantChange = (e) => {
    const variant = e.detail?.variant;
    if (!variant) return;
    this.variant = variant;
    if (!this.chosen) {
      this.buttons.forEach((b) => {
        b.removeAttribute("data-selected");
        b.setAttribute("aria-pressed", "false");
      });
      setTimeout(() => this.#stripVariantParam(), 0);
    }
    queueMicrotask(() => this.render());
  };

  #onSubmitCapture = (e) => {
    if (e.target !== this.form) return;
    const state = this.#state();
    if (state === "choose") {
      e.preventDefault();
      e.stopImmediatePropagation();
      this.nudge();
      return;
    }
    if (state === "notify") {
      e.preventDefault();
      e.stopImmediatePropagation();
      return;
    }
    document.dispatchEvent(
      new CustomEvent("rug:before-add", {
        detail: {
          quantity: this.quantity,
          sizeLabel: this.sizeLabel,
          variant: this.variant,
          productTitle: this.product?.title || "",
        },
      }),
    );
  };

  nudge() {
    this.classList.add("is-nudged");
    if (this.heading) this.heading.textContent = RR.t("please_choose_size");
    this.scrollIntoView({ behavior: "smooth", block: "center" });
    this.buttons.find((b) => b.dataset.available === "true")?.focus({ preventScroll: true });
  }

  #state() {
    if (!this.chosen || !this.variant) return "choose";
    const canSell = this.variant.available || this.variant.inventory_policy === "continue";
    return canSell ? "add" : "notify";
  }

  /* ---- Add-to-basket label: the theme resets it on every variant change, so re-apply ---- */

  #watchCartButton() {
    this.atc = this.form?.querySelector("add-to-cart-component");
    this.submit = this.atc?.querySelector('button[type="submit"]');
    this.submitLabel = this.submit?.querySelector(".button-label");
    if (!this.submitLabel) return;
    const d = this.atc.dataset;
    this.themeIdle = new Set(
      [d.textAvailable, d.textSoldOut, d.textUnavailable, d.textPreorder, "Add to cart", "Add to Cart", "Sold out", "Unavailable", "Pre-order"]
        .filter(Boolean)
        .map((t) => t.trim()),
    );
    this.observer = new MutationObserver(() => this.#applyCartButton());
    this.observer.observe(this.submitLabel, { childList: true, characterData: true, subtree: true });
  }

  #cartLabel(state) {
    if (state === "choose") return RR.t("choose_size");
    if (state === "notify") return RR.t("sold_out");
    return RR.t("add_to_basket");
  }

  #applyCartButton() {
    if (!this.submitLabel) return;
    const state = this.#state();
    const current = this.submitLabel.textContent.trim();
    const ours =
      current === this.lastLabel ||
      this.themeIdle.has(current) ||
      current === "" ||
      current.startsWith(RR.t("add_to_basket")) ||
      current === RR.t("choose_size") ||
      current.startsWith("Notify me") ||
      current === RR.t("sold_out");
    if (!ours) return;
    const label = this.#cartLabel(state);
    this.observer.disconnect();
    if (current !== label) this.submitLabel.textContent = label;
    this.lastLabel = label;
    this.submit.dataset.rugState = state;
    this.submit.disabled = state === "notify";
    this.observer.observe(this.submitLabel, { childList: true, characterData: true, subtree: true });
  }

  /* ---- Page render ---- */

  render() {
    const btn = this.selectedButton;
    const state = this.#state();

    if (this.heading) {
      if (btn) this.heading.textContent = RR.t("size_label", { size: this.#labelFor(btn) });
      else if (!this.classList.contains("is-nudged")) this.heading.textContent = this.headingDefault;
    }

    if (this.good && this.goodText) {
      const text = btn ? goodFor(+btn.dataset.w, +btn.dataset.l) : "";
      this.good.hidden = !text;
      this.goodText.textContent = text;
    }

    this.#renderPrice(btn);
    this.#applyCartButton();

    document.dispatchEvent(
      new CustomEvent("rug:state", {
        detail: {
          chosen: Boolean(btn),
          state,
          variant: this.variant,
          sizeLabel: btn ? this.#labelFor(btn) : "",
          w: btn ? +btn.dataset.w : 0,
          l: btn ? +btn.dataset.l : 0,
          quantity: this.quantity,
          cartLabel: this.#cartLabel(state),
          minPrice: this.product?.price_min ?? this.product?.price,
        },
      }),
    );
  }

  #renderPrice(btn) {
    const box = document.querySelector("[data-rug-price]");
    if (!box) return;
    const prefix = box.querySelector("[data-rug-price-prefix]");
    const now = box.querySelector("[data-rug-price-now]");
    const was = box.querySelector("[data-rug-price-was]");
    const save = box.querySelector("[data-rug-price-save]");
    const varies = box.dataset.varies === "true";
    if (!btn && varies) {
      prefix.hidden = false;
      now.textContent = money(+box.dataset.minPrice);
      now.classList.remove("rug-price__now--sale");
      was.hidden = true;
      save.hidden = true;
      return;
    }
    const v = this.variant;
    if (!v) return;
    prefix.hidden = true;
    now.textContent = money(v.price);
    const onSale = v.compare_at_price && v.compare_at_price > v.price;
    now.classList.toggle("rug-price__now--sale", Boolean(onSale));
    was.hidden = !onSale;
    save.hidden = !onSale;
    if (onSale) {
      was.textContent = money(v.compare_at_price);
      save.textContent = RR.t("save_amount", { amount: money(v.compare_at_price - v.price) });
    }
  }

  /* ---- Size guide drawer ---- */

  openGuide() {
    if (!this.drawer) return;
    this.lastFocus = document.activeElement;
    const fallback = this.buttons.find((b) => b.hasAttribute("data-popular")) || this.buttons[0];
    this.planId = (this.selectedButton || fallback)?.dataset.variantId;
    this.drawer.hidden = false;
    document.documentElement.style.overflow = "hidden";
    this.#renderPlanner();
    this.drawer.querySelector("[data-rug-guide-close].rug-drawer__close")?.focus();
  }

  closeGuide() {
    if (!this.drawer) return;
    this.drawer.hidden = true;
    document.documentElement.style.overflow = "";
    this.lastFocus?.focus?.();
  }

  #renderPlanner() {
    if (!this.planner) return;
    const btn = this.#buttonForVariant(this.planId) || this.buttons[0];
    if (!btn) return;
    const S = +btn.dataset.w;
    const L = +btn.dataset.l;
    if (!S || !L) return;
    const f = fmt(this.units);
    const p = place(this.room, S, L, f);
    const inset = 5;
    const t = TIERS[p.tier];
    const fill = (this.rugHex || "").trim() || "#ffffff";
    const roomLabel = ROOMS.find(([id]) => id === this.room)?.[1]?.toLowerCase() || this.room;

    this.planner.querySelectorAll("[data-rug-room]").forEach((el) => {
      el.setAttribute("aria-selected", String(el.dataset.rugRoom === this.room));
    });
    this.planner.querySelectorAll("[data-rug-furn]").forEach((g) => {
      g.toggleAttribute("hidden", g.dataset.rugFurn !== this.room);
    });

    const rug = this.planner.querySelector("[data-rug-plan-rug]");
    const inner = this.planner.querySelector("[data-rug-plan-rug-inner]");
    if (rug) {
      rug.setAttribute("x", p.x);
      rug.setAttribute("y", p.y);
      rug.setAttribute("width", p.w);
      rug.setAttribute("height", p.h);
      rug.setAttribute("fill", fill);
    }
    if (inner) {
      inner.setAttribute("x", p.x + inset);
      inner.setAttribute("y", p.y + inset);
      inner.setAttribute("width", Math.max(0, p.w - inset * 2));
      inner.setAttribute("height", Math.max(0, p.h - inset * 2));
      const key = fill.replace(/\s/g, "").toLowerCase();
      const light = key === "#fff" || key === "#ffffff" || key === "white" || key === "fff" || key === "ffffff";
      inner.setAttribute("stroke", light ? "#000000" : "#ffffff");
    }

    const svg = this.planner.querySelector("[data-rug-plan-svg]");
    if (svg) svg.setAttribute("aria-label", RR.t("plan_aria", { size: f.size(S, L), room: f.room(460, 380), name: roomLabel }));
    const roomText = this.planner.querySelector("[data-rug-plan-room]");
    if (roomText) roomText.textContent = RR.t("plan_room", { size: f.room(460, 380) });

    const verdict = this.planner.querySelector("[data-rug-verdict]");
    if (verdict) {
      verdict.dataset.tier = p.tier;
      verdict.style.background = t.bg;
      const badge = verdict.querySelector("[data-rug-verdict-tier]");
      const title = verdict.querySelector("[data-rug-verdict-title]");
      const body = verdict.querySelector("[data-rug-verdict-body]");
      if (badge) {
        badge.textContent = TIER_LABEL[p.tier] || p.tier;
        badge.style.background = t.fg;
      }
      if (title) title.textContent = p.title;
      if (body) body.textContent = p.body;
    }

    const chips = this.planner.querySelector("[data-rug-plan-sizes]");
    if (chips) {
      chips.innerHTML = this.buttons
        .map((b) => {
          const s = +b.dataset.w;
          const l = +b.dataset.l;
          if (!s || !l) return "";
          const q = place(this.room, s, l, f);
          const tt = TIERS[q.tier];
          const on = b === btn;
          return `<button type="button" class="rug-planner__chip" data-rug-plan-size="${b.dataset.variantId}" aria-pressed="${on}">
            <span class="rug-planner__chip-size">${f.short(s, l)}</span>
            <span class="rug-planner__chip-tier" style="color:${on ? "#fff" : tt.fg}"><span class="rug-planner__chip-dot" style="background:${tt.fg}"></span>${TIER_LABEL[q.tier] || q.tier}</span>
          </button>`;
        })
        .join("");
    }

    const tips = this.planner.querySelector("[data-rug-plan-tips]");
    if (tips) {
      tips.innerHTML = TIPS(f)[this.room].map((text, i) => `<li><span>${i + 1}</span>${text}</li>`).join("");
    }

    if (this.guideCta) this.guideCta.textContent = RR.t("choose_size_x", { size: this.#labelFor(btn) });
  }
}

/* ------------------------------------------------------------------ */
/* Stock line + back-in-stock request                                   */
/* ------------------------------------------------------------------ */

function initStock() {
  const el = document.querySelector("[data-rug-stock]");
  if (!el) return;
  const d = el.dataset;
  const dot = el.querySelector("[data-rug-stock-dot]");
  const title = el.querySelector("[data-rug-stock-title]");
  const detail = el.querySelector("[data-rug-stock-detail]");
  const notify = el.querySelector("[data-rug-notify]");
  const mtoDate = addWorkingDays(parseInt(d.mtoDays, 10) || 10);
  let sizeLabel = "";

  const set = (mode, t, text, showNotify) => {
    dot.className = `rug-stock__dot rug-stock__dot--${mode}`;
    title.textContent = t;
    detail.textContent = text;
    detail.hidden = !text;
    notify.hidden = true;
  };

  const base = () => {
    if (d.mode === "sold_out") set("sold_out", d.soldOutTitle, d.soldOutDetail, true);
    else if (d.mode === "made_to_order") set("made_to_order", d.mtoTitle, d.mtoDetail.replace("[date]", mtoDate), false);
    else set("in_stock", d.inStockTitle, d.inStockDetail, false);
  };
  base();

  document.addEventListener("rug:state", (e) => {
    const s = e.detail;
    sizeLabel = s.sizeLabel;
    if (d.mode === "sold_out") return base();
    if (s.chosen && s.state === "notify") {
      set("size_oos", RR.t("stock_size_oos", { size: s.sizeLabel }), d.oosDetail, true);
    } else {
      base();
    }
  });

  const submit = el.querySelector("[data-rug-notify-submit]");
  const email = el.querySelector("[data-rug-notify-email]");
  submit?.addEventListener("click", async () => {
    if (!email.value || !email.checkValidity()) {
      email.reportValidity();
      return;
    }
    submit.disabled = true;
    const body = new FormData();
    body.append("form_type", "contact");
    body.append("utf8", "✓");
    body.append("contact[email]", email.value);
    body.append("contact[Request]", "Back in stock");
    body.append("contact[Product]", d.productTitle);
    body.append("contact[Size]", sizeLabel || "Any size");
    body.append("contact[URL]", location.origin + d.productUrl);
    try {
      await fetch("/contact#contact_form", { method: "POST", body });
      el.querySelector("[data-rug-notify-row]").hidden = true;
      el.querySelector("[data-rug-notify-thanks]").hidden = false;
    } catch {
      submit.disabled = false;
    }
  });
}

/* ------------------------------------------------------------------ */
/* <rug-underlay>: sized underlay, added after the rug                  */
/* ------------------------------------------------------------------ */

class RugUnderlay extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    try {
      this.variants = JSON.parse(this.querySelector("[data-rug-underlay-variants]").textContent)
        .map((v) => {
          // "160cm x 230cm", "Select Length: 230cm / Select Width: 160cm" or "5' 3\" x 7' 7\"".
          const parts = String(v.title || "")
            .split(/\s*\/\s*|\s*×\s*|\s+x\s+|(?<=cm|ft|in|"|')\s*x\s*/i)
            .filter((part) => /\d/.test(part));
          const [a = 0, b = 0] = parts.map((part) => RR.toCm(part));
          return { ...v, parts, w: Math.min(a, b), l: Math.max(a, b) };
        })
        .filter((v) => v.available && v.w && v.l);
    } catch {
      this.variants = [];
    }
    this.check = this.querySelector("[data-rug-underlay-check]");
    this.priceEl = this.querySelector("[data-rug-underlay-price]");
    this.sizeEl = this.querySelector("[data-rug-underlay-size]");
    this.fromText = this.priceEl.textContent;
    this.match = null;
    this.pending = null;

    if (!this.variants.length) {
      this.hidden = true;
      return;
    }

    this.check.addEventListener("change", () => document.dispatchEvent(new Event("rug:underlay-change")));
    document.addEventListener("rug:state", (e) => this.#onState(e.detail));
    document.addEventListener("rug:before-add", (e) => {
      this.pending =
        this.check.checked && this.match
          ? { id: this.match.id, quantity: e.detail.quantity, forLabel: `${e.detail.productTitle} ${e.detail.sizeLabel}`.trim() }
          : null;
      if (this.pending) {
        clearTimeout(this.pendingTimer);
        this.pendingTimer = setTimeout(() => (this.pending = null), 8000);
      }
    });
    ThemeBus.addEventListener(EVENTS.cartItemAdded, () => this.#addPending());
  }

  get checkedPrice() {
    return this.check?.checked && this.match ? this.match.price : 0;
  }

  #onState(s) {
    if (!s.chosen) {
      this.match = null;
      this.priceEl.textContent = this.fromText;
      this.sizeEl.hidden = true;
      this.hidden = false;
      return;
    }
    const rw = Math.min(s.w, s.l);
    const rl = Math.max(s.w, s.l);
    const fits = this.variants
      .filter((v) => v.w >= rw * 0.8 && v.l >= rl * 0.8)
      .sort((a, b) => a.w * a.l - b.w * b.l);
    this.match = fits[0] || null;
    this.hidden = !this.match;
    if (!this.match) {
      if (this.check.checked) {
        this.check.checked = false;
        document.dispatchEvent(new Event("rug:underlay-change"));
      }
      return;
    }
    this.priceEl.textContent = `+${money(this.match.price)}`;
    const sizeText = this.match.parts.map((part) => RR.label(part)).filter(Boolean).join(" × ") || this.match.title;
    this.sizeEl.textContent = RR.t("underlay_fits_rug", { size: sizeText });
    this.sizeEl.hidden = false;
  }

  async #addPending() {
    const item = this.pending;
    if (!item) return;
    this.pending = null;
    clearTimeout(this.pendingTimer);
    try {
      await fetch(shopifyCartUrl("cart/add.js"), {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items: [{ id: item.id, quantity: item.quantity, properties: { _For: item.forLabel } }] }),
      });
      const res = await fetch(shopifyCartUrl("cart.js"));
      if (res.ok) window.__cart = await res.json();
      updateCartBubble();
      ThemeBus.dispatchEvent(new Event(EVENTS.cartUpdated));
    } catch (err) {
      console.error("[RugPDP] Underlay add failed", err);
    }
    this.check.checked = false;
    document.dispatchEvent(new Event("rug:underlay-change"));
  }
}

/* ------------------------------------------------------------------ */
/* <rug-sticky-bar>: mobile basket bar                                  */
/* ------------------------------------------------------------------ */

class RugStickyBar extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    this.size = this.querySelector("[data-rug-sticky-size]");
    this.price = this.querySelector("[data-rug-sticky-price]");
    this.cta = this.querySelector("[data-rug-sticky-cta]");
    document.body.appendChild(this);

    document.addEventListener("rug:state", (e) => {
      const s = e.detail;
      this.size.textContent = s.chosen ? ` · ${s.sizeLabel}` : "";
      this.price.textContent = s.chosen && s.variant ? money(s.variant.price) : RR.t("from_price", { price: money(s.minPrice) });
      this.cta.textContent = s.state === "choose" ? RR.t("choose_size") : s.state === "notify" ? RR.t("sold_out") : RR.t("add_to_basket");
      this.cta.dataset.rugState = s.state;
      this.cta.disabled = s.state === "notify";
    });

    const target = document.querySelector("product-form-component add-to-cart-component");
    if (!target || !("IntersectionObserver" in window)) return;
    let passed = false;
    new IntersectionObserver(([entry]) => {
      passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      this.classList.toggle("is-visible", passed);
      this.setAttribute("aria-hidden", String(!passed));
      this.cta.tabIndex = passed ? 0 : -1;
    }).observe(target);
  }
}

/* ------------------------------------------------------------------ */
/* Small behaviours                                                     */
/* ------------------------------------------------------------------ */

/**
 * A fast swipe on an overflow strip (or a scroll that starts on a swatch) must
 * not follow the link. A real tap moves under ~8px and still navigates.
 */
function bindDragClickGuard(scroller, selector) {
  if (!scroller || scroller.dataset.rrDragGuard) return;
  scroller.dataset.rrDragGuard = "1";
  let state = null;

  const pointOf = (event) => (event.touches ? event.touches[0] : event);

  const begin = (event) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    const point = pointOf(event);
    if (!point) return;
    state = {
      x: point.clientX,
      y: point.clientY,
      scrollY: window.scrollY,
      left: scroller.scrollLeft,
      moved: false,
    };
  };

  const move = (event) => {
    if (!state) return;
    const point = pointOf(event);
    if (!point) return;
    if (
      dragClickShouldSuppress({
        dx: point.clientX - state.x,
        dy: point.clientY - state.y,
        scrollDx: scroller.scrollLeft - state.left,
        scrollDy: window.scrollY - state.scrollY,
      })
    ) {
      state.moved = true;
    }
  };

  scroller.addEventListener("pointerdown", begin);
  scroller.addEventListener("pointermove", move);
  scroller.addEventListener("touchstart", begin, { passive: true });
  scroller.addEventListener("touchmove", move, { passive: true });
  scroller.addEventListener(
    "click",
    (event) => {
      if (!state) return;
      const block =
        state.moved ||
        dragClickShouldSuppress({
          dx: 0,
          dy: 0,
          scrollDx: scroller.scrollLeft - state.left,
          scrollDy: window.scrollY - state.scrollY,
        });
      state = null;
      if (!block) return;
      const hit = event.target.closest(selector);
      if (!hit || !scroller.contains(hit)) return;
      event.preventDefault();
    },
    true
  );
}

function colourMoreCount(toggle) {
  const mobile = window.matchMedia("(max-width: 759px)").matches;
  if (mobile && toggle.dataset.moreMobile) return toggle.dataset.moreMobile;
  return toggle.dataset.more;
}

function initColours() {
  const mobileQuery = window.matchMedia("(max-width: 759px)");
  document.querySelectorAll("[data-rug-colours]").forEach((root) => {
    const toggle = root.querySelector("[data-rug-colour-toggle]");
    if (!toggle) return;
    const dot = toggle.querySelector(".rug-colours__more-dot");
    const label = toggle.querySelector(".rug-colours__more-label");
    const paint = () => {
      const open = toggle.getAttribute("aria-expanded") === "true";
      root.toggleAttribute("data-open", open);
      dot.textContent = open ? "−" : colourMoreCount(toggle);
      label.textContent = open ? RR.t("less") : RR.t("more");
    };
    toggle.addEventListener("click", () => {
      const open = toggle.getAttribute("aria-expanded") !== "true";
      toggle.setAttribute("aria-expanded", String(open));
      root.querySelectorAll("[data-rug-colour-extra]").forEach((el) => (el.hidden = !open));
      paint();
      root.querySelector(".rug-colours__grid").appendChild(toggle);
    });
    mobileQuery.addEventListener("change", paint);
    paint();
  });
  document.querySelectorAll("[data-rug-colours]").forEach((root) => {
    bindDragClickGuard(root, "a[data-rr-colour-swatch]");
  });
}

function initRangeScroll() {
  document.querySelectorAll(".rug-range").forEach((root) => {
    const scroller = root.querySelector("[data-rug-range-scroll]");
    const bar = root.querySelector("[data-rug-range-bar]");
    const thumb = root.querySelector("[data-rug-range-thumb]");
    if (!scroller || !bar || !thumb) return;

    const maxScroll = () => scroller.scrollWidth - scroller.clientWidth;
    const trackRoom = () => Math.max(0, bar.clientWidth - thumb.offsetWidth);

    const sync = () => {
      const overflow = scroller.scrollWidth > scroller.clientWidth + 1;
      root.toggleAttribute("data-range-overflow", overflow);
      bar.hidden = !overflow;
      scroller.tabIndex = overflow ? 0 : -1;
      if (!overflow) return;
      const ratio = scroller.clientWidth / scroller.scrollWidth;
      thumb.style.width = `${Math.max(36, bar.clientWidth * ratio)}px`;
      const room = trackRoom();
      const left = maxScroll() > 0 ? (scroller.scrollLeft / maxScroll()) * room : 0;
      thumb.style.transform = `translateX(${left}px)`;
    };

    const scrollFromClientX = (clientX) => {
      const room = trackRoom();
      if (room <= 0) return;
      const x = clientX - bar.getBoundingClientRect().left - thumb.offsetWidth / 2;
      scroller.scrollLeft = (Math.min(room, Math.max(0, x)) / room) * maxScroll();
    };

    scroller.addEventListener("scroll", sync, { passive: true });
    scroller.setAttribute("aria-label", RR.t("more_colours"));
    window.addEventListener("resize", sync);
    if (typeof ResizeObserver !== "undefined") new ResizeObserver(sync).observe(scroller);

    let dragging = false;
    thumb.addEventListener("pointerdown", (event) => {
      dragging = true;
      thumb.setPointerCapture(event.pointerId);
      scrollFromClientX(event.clientX);
      event.preventDefault();
    });
    thumb.addEventListener("pointermove", (event) => {
      if (dragging) scrollFromClientX(event.clientX);
    });
    const stopDrag = () => {
      dragging = false;
    };
    thumb.addEventListener("pointerup", stopDrag);
    thumb.addEventListener("pointercancel", stopDrag);
    bar.addEventListener("pointerdown", (event) => {
      if (event.target === thumb) return;
      scrollFromClientX(event.clientX);
    });

    sync();
    bindDragClickGuard(scroller, "a.rug-range__card");
  });
}

function initGallery() {
  if (!document.body.hasAttribute("data-rug-pdp")) return;
  const wrap = document.querySelector(".product-media__wrapper");
  const thumbsEl = document.querySelector(".product-media-navigation__wrapper-mobile");
  if (!wrap) return;
  if (!wrap.swiper) {
    if (initGallery.tries > 40) return;
    initGallery.tries = (initGallery.tries || 0) + 1;
    setTimeout(initGallery, 80);
    return;
  }

  const main = wrap.swiper;
  const thumbs = thumbsEl?.swiper;

  const bindThumbs = () => {
    if (!thumbs) return;
    thumbs.params.slidesPerView = "auto";
    thumbs.params.spaceBetween = 8;
    thumbs.params.watchSlidesProgress = true;
    thumbs.params.slideToClickedSlide = true;
    thumbs.params.preventClicks = false;
    thumbs.params.preventClicksPropagation = false;
    thumbs.update();
    main.params.thumbs = { swiper: thumbs };
    if (main.thumbs) {
      main.thumbs.swiper = thumbs;
      try {
        main.thumbs.init();
      } catch (_) {}
      main.thumbs.update(true);
    }
  };
  bindThumbs();
  main.on("resize", bindThumbs);

  if (thumbsEl && !thumbsEl.dataset.rugThumbsBound) {
    thumbsEl.dataset.rugThumbsBound = "true";
    thumbsEl.addEventListener("click", (e) => {
      const slide = e.target.closest(".swiper-slide");
      if (!slide || !thumbsEl.contains(slide)) return;
      const mediaId = slide.dataset.mediaId;
      const realSlides = [...wrap.querySelectorAll(".swiper-wrapper > .swiper-slide:not(.swiper-slide-duplicate)")];
      const index = mediaId
        ? realSlides.findIndex((s) => s.dataset.mediaId === mediaId)
        : [...thumbsEl.querySelectorAll(".swiper-wrapper > .swiper-slide")].indexOf(slide);
      if (index < 0) return;
      e.preventDefault();
      if (main.params.loop && typeof main.slideToLoop === "function") main.slideToLoop(index);
      else main.slideTo(index);
    });
  }

  const host = wrap.closest("product-media-component") || wrap.parentElement;
  let count = host.querySelector(".rug-gallery__count");
  if (!count) {
    count = document.createElement("span");
    count.className = "rug-gallery__count";
    host.appendChild(count);
  }
  const paint = () => {
    const total = wrap.querySelectorAll(".swiper-slide:not(.swiper-slide-duplicate)").length
      || main.slides?.length
      || 1;
    const current = (main.realIndex ?? main.activeIndex ?? 0) + 1;
    count.textContent = `${current} / ${total}`;
  };
  paint();
  main.on("slideChange", paint);
}
initGallery.tries = 0;

function polarisBlock() {
  return document.querySelector("[data-rug-reviews-product]")?.children?.[0] || null;
}

function paintProductRating(polaris, count, average) {
  const link = document.querySelector("a.rug-rating");
  if (!link) return;
  const summary = polaris?.querySelector('[aria-label^="Overall product rating"]');
  const score = average || summary?.getAttribute("aria-label")?.match(/([\d.]+)\s*$/)?.[1];
  if (!score) return;
  const value = Number(score);
  const label = [...link.children].find((el) => el.matches("span:not(.rug-stars)"));
  if (label) label.textContent = RR.t("rating_line", { rating: RR.num(value), count, reviews: count === 1 ? RR.t("review") : RR.t("reviews") });
  const stars = link.querySelector(".rug-stars");
  const fill = link.querySelector(".rug-stars__fill");
  if (stars) stars.setAttribute("aria-label", RR.t("stars_aria", { value: RR.num(value) }));
  if (fill) fill.style.width = `${Math.min(100, value * 20)}%`;
}

function tidyReviewCards() {
  const product = (document.querySelector("h1")?.textContent || "").trim();
  document.querySelectorAll(".rug-reviews .item__review .R-TextHeading").forEach((heading) => {
    const text = (heading.textContent || "").trim();
    const isProduct = product && text.startsWith(product);
    if (!isProduct || heading.hidden) return;
    heading.hidden = true;
  });
  document.querySelectorAll(".rug-reviews .item__review .R-TextBody").forEach((body) => {
    if (body.classList.contains("is-quiet")) return;
    if (!/didn't leave any comments/i.test(body.textContent || "")) return;
    body.textContent = RR.t("reviews_star_only");
    body.classList.add("is-quiet");
  });
  const score = document.querySelector(".rug-reviews .header__group--left .R-TextHeading--md");
  if (score && /^\d+\.\d+$/.test((score.textContent || "").trim())) {
    score.textContent = Number(score.textContent).toFixed(1);
  }
}

function paintScoreStars(value) {
  const host = document.querySelector("[data-rug-score-stars]");
  const stars = host?.querySelector(".rug-stars");
  const fill = host?.querySelector(".rug-stars__fill");
  if (stars) stars.setAttribute("aria-label", RR.t("stars_aria", { value: RR.num(value) }));
  if (fill) fill.style.width = `${Math.min(100, value * 20)}%`;
}

function applyProductStats(stats) {
  const root = document.querySelector(".rug-reviews");
  if (!root || !stats) return;
  root.dataset.reviewStats = "product";
  const noun = root.dataset.reviewNoun || RR.t("noun_rug");
  const count = stats.count;
  root.dataset.productReviews = String(count);
  if (!count) {
    root.dataset.productReviews = "0";
    root.querySelector("[data-rug-scoreline]")?.setAttribute("hidden", "");
    root.querySelector("[data-rug-based]")?.setAttribute("hidden", "");
    root.querySelector("[data-rug-bars]")?.setAttribute("hidden", "");
    root.querySelector(".rug-reviews__store")?.setAttribute("hidden", "");
    root.querySelector(".rug-reviews__empty")?.setAttribute("hidden", "");
    return;
  }
  root.querySelector(".rug-reviews__store")?.removeAttribute("hidden");
  root.querySelector(".rug-reviews__empty")?.setAttribute("hidden", "");
  const scoreEl = root.querySelector("[data-rug-score]");
  if (scoreEl) scoreEl.textContent = stats.average.toFixed(1);
  paintScoreStars(stats.average);
  root.querySelector("[data-rug-scoreline]")?.removeAttribute("hidden");
  const based = root.querySelector("[data-rug-based]");
  if (based) {
    based.textContent = RR.t("reviews_based", { count, reviews: count === 1 ? RR.t("review") : RR.t("reviews"), noun });
    based.removeAttribute("hidden");
  }
  const max = Math.max(...stats.buckets, 0);
  const bars = root.querySelector("[data-rug-bars]");
  if (bars && max) {
    bars.querySelectorAll("[data-rug-bar]").forEach((row) => {
      const n = stats.buckets[Number(row.dataset.rugBar) - 1] || 0;
      const countEl = row.querySelector("[data-rug-bar-count]");
      const fill = row.querySelector("[data-rug-bar-fill]");
      if (countEl) countEl.textContent = String(n);
      if (fill) fill.style.width = `${(n / max) * 100}%`;
    });
    bars.removeAttribute("hidden");
  }
  paintProductRating(polarisBlock(), count, stats.average);
}

async function loadProductReviewStats(root) {
  const store = root.dataset.reviewsStore;
  const sku = root.dataset.productId;
  if (!store || !sku) return null;
  const url = `https://api.reviews.io/reviews?store=${encodeURIComponent(store)}&sku=${encodeURIComponent(sku)}&type=product_review&per_page=1`;
  const res = await fetch(url);
  if (!res.ok) return null;
  const data = await res.json();
  const ratings = data.stats?.ratings || {};
  const average = Number(data.average_rating || 0);
  return {
    count: Number(data.review_count || 0),
    average: Number.isFinite(average) ? average : 0,
    buckets: [1, 2, 3, 4, 5].map((star) => Number(ratings[String(star)] || ratings[star] || 0)),
  };
}

function reviewTotal(polaris) {
  const line = [...(polaris?.querySelectorAll(".header__group--left .R-TextBody") || [])].find((el) =>
    /based on\s+\d+/i.test(el.textContent || "") || /\d+\s+review/i.test(el.textContent || "")
  );
  const text = line?.textContent || "";
  const match = text.match(/based on\s+(\d+)/i) || text.match(/(\d+)\s+review/i);
  return match ? Number(match[1]) : 0;
}

function reviewBuckets(polaris) {
  const group = [...(polaris?.querySelectorAll(".c-filters__dropdown__group") || [])].find((el) =>
    /breakdown/i.test(el.textContent || "")
  );
  const nums = [...(group?.querySelectorAll(".R-TextBody") || [])]
    .map((el) => Number((el.textContent || "").trim()))
    .filter((n) => Number.isFinite(n));
  if (nums.length >= 5) return [nums[4], nums[3], nums[2], nums[1], nums[0]];
  const buckets = [0, 0, 0, 0, 0];
  polaris?.querySelectorAll(".R-ContentList__item").forEach((item) => {
    const full = item.querySelectorAll(".item__review .stars__icon--100").length;
    if (full >= 1 && full <= 5) buckets[full - 1] += 1;
  });
  return buckets;
}

function paintReviewSide(polaris, count) {
  const root = document.querySelector(".rug-reviews");
  if (!root || !polaris || root.dataset.reviewStats === "product") return;
  const summary = polaris.querySelector('[aria-label^="Overall product rating"]');
  const score = summary?.getAttribute("aria-label")?.match(/([\d.]+)\s*$/)?.[1];
  const noun = root.dataset.reviewNoun || RR.t("noun_rug");
  if (score) {
    const value = Number(score);
    const scoreEl = root.querySelector("[data-rug-score]");
    if (scoreEl) scoreEl.textContent = value.toFixed(1);
    paintScoreStars(value);
    root.querySelector("[data-rug-scoreline]")?.removeAttribute("hidden");
  }
  const total = reviewTotal(polaris) || count || 0;
  if (total) {
    const based = root.querySelector("[data-rug-based]");
    if (based) based.textContent = RR.t("reviews_based", { count: total, reviews: total === 1 ? RR.t("review") : RR.t("reviews"), noun });
    based?.removeAttribute("hidden");
  }
  const buckets = reviewBuckets(polaris);
  const max = Math.max(...buckets);
  const bars = root.querySelector("[data-rug-bars]");
  if (!bars || !max) return;
  bars.querySelectorAll("[data-rug-bar]").forEach((row) => {
    const n = buckets[Number(row.dataset.rugBar) - 1] || 0;
    const countEl = row.querySelector("[data-rug-bar-count]");
    const fill = row.querySelector("[data-rug-bar-fill]");
    if (countEl) countEl.textContent = String(n);
    if (fill) fill.style.width = `${(n / max) * 100}%`;
  });
  bars.removeAttribute("hidden");
}

function dressReviewCount() {
  const polaris = polarisBlock();
  const line = [...(polaris?.querySelectorAll(".header__group--left .R-TextBody") || [])].find((el) =>
    /based on/i.test(el.textContent || "")
  );
  if (!line || line.dataset.rugDressed) return;
  const count = (line.textContent || "").match(/(\d+)/)?.[1];
  if (!count) return;
  const noun = document.body.hasAttribute("data-stair-pdp") ? RR.t("noun_runner") : RR.t("noun_rug");
  line.textContent = RR.t("reviews_of_this", { count, reviews: count === "1" ? RR.t("review") : RR.t("reviews"), noun });
  line.dataset.rugDressed = "1";
}

function settleUnknownReviews(root) {
  let attempts = 0;
  const timer = setInterval(() => {
    attempts += 1;
    const polaris = polarisBlock();
    const items = polaris?.querySelectorAll(".R-ContentList__item").length || 0;
    const empty = polaris?.querySelector('[aria-label*="No reviews collected" i]');
    if (items > 0) {
      if (root.dataset.reviewStats !== "product") {
        const count = reviewTotal(polaris) || items;
        root.dataset.productReviews = String(count);
        paintReviewSide(polaris, count);
        paintProductRating(polaris, count);
      }
      root.querySelector(".rug-reviews__empty")?.setAttribute("hidden", "");
      dressReviewCount();
      tidyReviewCards();
      clearInterval(timer);
      return;
    }
    if (empty && attempts >= 8) {
      if (root.dataset.reviewStats !== "product") root.dataset.productReviews = "0";
      if (polaris) polaris.inert = true;
      clearInterval(timer);
      return;
    }
    if (attempts >= 40) clearInterval(timer);
  }, 200);
}

function initReviewsFallback() {
  const root = document.querySelector(".rug-reviews");
  if (root?.dataset.productId) {
    loadProductReviewStats(root).then(applyProductStats).catch(() => {});
  }
  const tidyTimer = setInterval(() => {
    const polaris = polarisBlock();
    tidyReviewCards();
    dressReviewCount();
    if (polaris?.querySelector(".R-ContentList__item") && document.querySelector(".rug-reviews")?.dataset.reviewStats !== "product") {
      const count = reviewTotal(polaris);
      paintReviewSide(polaris, count);
      if (count) paintProductRating(polaris, count);
    }
  }, 400);
  setTimeout(() => clearInterval(tidyTimer), 8000);
  dressReviewCount();
  const unknown = document.querySelector('.rug-reviews[data-product-reviews="unknown"]');
  if (unknown) {
    settleUnknownReviews(unknown);
    return;
  }
  const emptyRoot = document.querySelector('.rug-reviews[data-product-reviews="0"]');
  if (!emptyRoot) return;
  const polaris = polarisBlock();
  if (polaris) polaris.inert = true;
}

function findReviewsIoWriteControl(root) {
  if (!root) return null;
  const labelled = root.querySelector(
    '[aria-label*="Be the first to write a review" i], [aria-label*="Write a review" i], [aria-label*="Write Review" i]'
  );
  if (labelled) return labelled;
  return [...root.querySelectorAll('[role="button"], button, a')].find((el) =>
    /be the first to write a review|write a review|write review/i.test((el.textContent || "").trim())
  ) || null;
}

function sizeReviewsIoForm() {
  const root = document.querySelector(".rug-reviews");
  const panel = root?.querySelector(".rug-reviews__first");
  const polaris = polarisBlock();
  if (!root || !polaris) return;
  const width = (panel || root).clientWidth;
  if (!width) return;
  polaris.style.width = `${width}px`;
  polaris.style.maxWidth = `${width}px`;
}

function openReviewsIoForm() {
  const root = document.querySelector(".rug-reviews");
  const widget = document.getElementById("rug-reviews-widget");
  root?.classList.add("rug-reviews--write");
  const polaris = polarisBlock();
  if (polaris) polaris.inert = false;
  sizeReviewsIoForm();

  const tryClick = (attempt = 0) => {
    const write = findReviewsIoWriteControl(widget);
    if (write) {
      write.click();
      requestAnimationFrame(() => {
        sizeReviewsIoForm();
        widget?.querySelectorAll('[aria-label*="No reviews collected" i]').forEach((el) => {
          const box = el.closest(".u-textCenter--all") || el.parentElement;
          const prev = box?.previousElementSibling;
          if (prev?.classList.contains("u-hr")) prev.hidden = true;
          if (box) box.hidden = true;
        });
      });
      return;
    }
    if (attempt < 25) setTimeout(() => tryClick(attempt + 1), 160);
  };
  tryClick();
}

function initReviewLink() {
  document.addEventListener("click", (e) => {
    const link = e.target.closest("[data-rug-write-review]");
    if (!link) return;
    e.preventDefault();
    openReviewsIoForm();
  });
}

function initDesktopSticky() {
  const images = document.querySelector(".product-images");
  const buy = document.querySelector(".product-content-container");
  if (!images || !buy) return;
  const sync = () => {
    if (!window.matchMedia("(min-width: 760px)").matches) {
      document.body.removeAttribute("data-buy-sticky");
      return;
    }
    document.body.toggleAttribute("data-buy-sticky", buy.offsetHeight < images.offsetHeight);
  };
  const observer = new ResizeObserver(sync);
  observer.observe(images);
  observer.observe(buy);
  window.addEventListener("resize", sync);
  sync();
}

initStock();
initColours();
initRangeScroll();
initGallery();
initDesktopSticky();
initReviewsFallback();
initReviewLink();
if (!customElements.get("rug-underlay")) customElements.define("rug-underlay", RugUnderlay);
if (!customElements.get("rug-sticky-bar")) customElements.define("rug-sticky-bar", RugStickyBar);
// Defined last: it broadcasts the first rug:state, so the listeners above must already exist.
if (!customElements.get("rug-size-selector")) customElements.define("rug-size-selector", RugSizeSelector);
if (!document.querySelector("rug-size-selector") && !document.body.hasAttribute("data-stair-pdp")) {
  document.dispatchEvent(new CustomEvent("rug:state", { detail: { chosen: true, state: "add", sizeLabel: "" } }));
}
